import * as React from 'react';
import { createPortal } from 'react-dom';
import TextAncestorContext from 'react-native-web/dist/exports/Text/TextAncestorContext';

import { PortalContext } from '@gitbook/spine-react';
import { useIsHydrating } from '@gitbook/util-react-hooks';

import { ResetInteractiveHandles } from '../Interactive';
import { ResetLayoutTheme } from '../theme';

export interface PortalAbsoluteRef {
    /** Return the DOM element wrapping this portal */
    getContainer: () => HTMLElement | null;
}

/*
 * Primitive portal component to render an absolute portal.
 */
const PortalAbsolute = React.forwardRef(
    (
        props: {
            children: React.ReactNode;
            /**
             * Optional container in which the portal should be rendered.
             * Ex: when a palette is inside a parent portal, it needs to be
             * rendered inside its parent portal container to handle events correctly.
             */
            container?: HTMLElement | null;
        },
        ref: React.Ref<PortalAbsoluteRef>
    ): React.ReactElement | null => {
        const { children, container: providedContainer } = props;

        const getPortalContainer = React.useContext(PortalContext);
        const container = providedContainer ?? getPortalContainer() ?? document.body;
        const hydrating = useIsHydrating();

        React.useImperativeHandle(ref, () => ({
            // Get the portal container,
            getContainer: getPortalContainer,
        }));

        if (!container || hydrating) {
            return null;
        }

        // we only create the portal when the component is mounted
        // otherwise it creates issues with rehydratation (https://github.com/facebook/react/issues/13097#issuecomment-405658104)
        return (
            <ResetLayoutTheme>
                {/* We reset the RNW internal context to indicate that Text in the portal are not considered
                    as nested into a Text when the portal is rendered in a Text */}
                <TextAncestorContext.Provider value={false}>
                    <ResetInteractiveHandles>
                        {createPortal(children, container)}
                    </ResetInteractiveHandles>
                </TextAncestorContext.Provider>
            </ResetLayoutTheme>
        );
    }
);
PortalAbsolute.displayName = 'PortalAbsolute';

export { PortalAbsolute };
